import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@ainkauf/core";
import { TransportError } from "@ainkauf/core";
import type { RemoteAdapter, SyncedRow, SyncedTable } from "@ainkauf/core";

/**
 * Die Gegenstelle. Hochladen ist ein Upsert auf die vom Client erzeugte ID,
 * damit ein wiederholter Versuch nach einem Abbruch keine Dublette erzeugt.
 * Herunterladen läuft über updated_at als Cursor, aufsteigend sortiert, damit
 * eine abgebrochene Seite beim nächsten Lauf lückenlos weitergeht.
 *
 * Zum Typisieren: Die Sync-Engine ist absichtlich generisch über alle
 * Tabellen, der erzeugte Datenbanktyp beschreibt jede Tabelle einzeln. Beides
 * lässt sich nicht vereinen, ohne die Engine pro Tabelle zu duplizieren.
 * Deshalb wird genau hier einmal entkoppelt, an einer benannten Stelle mit
 * Begründung. Die Abfragen der Oberfläche (Mitglieder, RPCs) bleiben voll
 * typisiert.
 */
/**
 * Lag es an der Leitung oder an den Daten?
 *
 * supabase-js meldet einen abgebrochenen fetch ohne Code, PostgREST dagegen
 * mit fünfstelligem Code. Alles ohne Code und alles ab 500 gilt als Transport,
 * der Rest als Absage an genau diese Zeilen.
 */
function alsFehler(table: SyncedTable, error: { message: string; code?: string | null }): Error {
  const code = error.code ?? "";
  const transport = code.length === 0 || /^5\d\d$/u.test(code) || code === "PGRST000";
  const text = `${table}: ${error.message}`;
  return transport ? new TransportError(text) : new Error(text);
}

export class SupabaseRemote implements RemoteAdapter {
  private readonly generic: SupabaseClient;

  constructor(client: SupabaseClient<Database>) {
    this.generic = client as unknown as SupabaseClient;
  }

  async upsert<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void> {
    if (rows.length === 0) return;
    const { error } = await this.generic.from(table).upsert([...rows], { onConflict: "id" });
    if (error) throw alsFehler(table, error);
  }

  /**
   * Ändert vorhandene Zeilen. Für Tabellen ohne insert-Policy, etwa families:
   * dort würde ein Upsert an der fehlenden Erlaubnis scheitern, obwohl nur
   * ein Feld geändert werden soll.
   */
  async update<T extends SyncedRow>(table: SyncedTable, rows: readonly T[]): Promise<void> {
    for (const row of rows) {
      const { id, ...rest } = row;
      const { error } = await this.generic.from(table).update(rest as Record<string, unknown>).eq("id", id);
      if (error) throw alsFehler(table, error);
    }
  }

  async fetchSince<T extends SyncedRow>(table: SyncedTable, since: string | null, limit: number): Promise<T[]> {
    let query = this.generic.from(table).select("*").order("updated_at", { ascending: true }).limit(limit);
    if (since !== null) query = query.gt("updated_at", since);

    const { data, error } = await query;
    if (error) throw alsFehler(table, error);
    return (data ?? []) as T[];
  }
}
